import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { MONTHS_PER_CYCLE } from "../pricing";
import { fetchPrepaidQuote } from "../services/prepaidService";
import type {
  AppliedCoupon,
  BillingCycleId,
  PriceBreakdown,
} from "../types/billing.types";
import type { PrepaidQuote } from "../types/prepaid.types";

export type QuoteStatus = "loading" | "ready" | "failed";

export interface QuoteState {
  /** What the order comes to, or `null` until the first quote lands. */
  price: PriceBreakdown | null;
  /** The coupon the server allowed, which is the only coupon there is. */
  coupon: AppliedCoupon | null;
  /**
   * The server's whole answer, which the summary does not need and the
   * purchase cannot do without.
   *
   * `price` above is the arithmetic, in the shape every summary on this screen
   * already draws. This is what the arithmetic was *about*: which kind of
   * purchase the server made of it, the dates it would buy, the credit behind
   * any figure taken off — and `amountDue`, which is what goes up as
   * `expected_total` when the order is created. See `createPrepaidOrder`, which
   * takes this and not a plan and a number.
   */
  quote: PrepaidQuote | null;
  status: QuoteStatus;
  error?: string;
  /** A re-quote is with the server — the coupon row stands down while it is. */
  pricing: boolean;
  /**
   * Prices the plan again with a code on it.
   *
   * The code goes to the server and the figures are replaced by what comes
   * back. A code that does not hold throws, carrying the server's own reason
   * for the row to print — and the figures on screen are left exactly as they
   * were, because a refusal is not a re-pricing.
   */
  apply: (code: string) => Promise<AppliedCoupon | null>;
  /** Prices the same plan again with no code on it, which is what taking a
   *  coupon off means: the server works out the order without it. */
  remove: () => Promise<void>;
  reload: () => void;
}

/**
 * The server's answer in the shape the summary already draws.
 *
 * Only two of these are a translation rather than a copy. `taxRate` is a
 * percent on the wire and a fraction here, because the summary writes it back
 * out as "GST 18%". And `renewsOn` is the term's own last day, as the server
 * dated it — not a renewal, since nothing renews now: it is the day access
 * runs out, which is what the timeline under the total is marking.
 *
 * `total` is `amountDue` and not `total_amount`. They are the same figure
 * today, and the one that is charged is this one — a summary quoting the other
 * would be a page that had not actually checked.
 */
const toBreakdown = (quote: PrepaidQuote): PriceBreakdown => ({
  months: quote.price.months,
  subtotal: quote.price.base,
  discount: quote.price.discount,
  taxRate: Number.parseFloat(quote.price.taxRateLabel) / 100 || 0,
  taxableAmount: quote.price.taxable,
  tax: quote.price.tax,
  total: quote.price.amountDue,
  renewsOn: new Date(`${quote.endsOn}T00:00:00`),
});

const toApplied = (quote: PrepaidQuote): AppliedCoupon | null =>
  quote.coupon ? { code: quote.coupon, discount: quote.price.discount } : null;

/**
 * What this term costs today, priced by the backend, with whatever coupon is on
 * the order.
 *
 * `POST /billing/prepaid/quote/`. It replaced `POST /billing/quote/`, which
 * answers `404 plan_not_found` for a prepaid plan code — the recurring quote
 * knows only the mandate plans, and those are not what this app sells any more.
 *
 * The coupon lives here rather than in the screen, and that is the whole design
 * of it: what is on the order is whatever the last quote came back carrying,
 * and nothing the page believes about a code survives being contradicted by it.
 * A code the buyer typed that the server would not take never becomes state —
 * `apply` throws and the row reports it, while the figures stay as they were.
 *
 * **Quote again before buying.** The credit for a term being cut short is
 * counted in whole IST days, so it drops at each midnight there, and an order
 * created against a figure from before one is refused with
 * `prepaid_quote_changed`. `quote` above is what the purchase is made from, so
 * the order is always made from the answer the buyer was actually shown.
 *
 * Waits for a plan. The price list is itself a fetch, so there is a moment with
 * no code to quote — see `usePlans` — and asking for a quote on `undefined`
 * would be a 400 for a mistake nobody made.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useQuote(planCode?: string, cycle?: BillingCycleId): QuoteState {
  const getToken = useTokenGetter();

  const [quote, setQuote] = useState<PrepaidQuote | null>(null);
  const [status, setStatus] = useState<QuoteStatus>("loading");
  const [error, setError] = useState<string | undefined>(undefined);
  const [pricing, setPricing] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // No plan yet. Still `loading` rather than `failed`: nothing has been asked
    // and nothing has been refused, and the screen is already waiting on the
    // price list this will follow.
    if (!planCode || !cycle) return;

    const controller = new AbortController();
    setStatus("loading");

    // The opening quote carries no code. A coupon applied to one term does not
    // follow the buyer to another — the Edit link goes back to the plans and
    // comes forward on a different plan code, and what held there may not hold
    // here. The server is asked afresh, and the coupon goes with it so the row
    // cannot keep showing a discount that is no longer on the order.
    fetchPrepaidQuote(
      getToken,
      { planCode, months: MONTHS_PER_CYCLE[cycle] },
      controller.signal
    )
      .then((quoted) => {
        if (controller.signal.aborted) return;
        setQuote(quoted);
        setError(undefined);
        setStatus("ready");
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setStatus("failed");
        setError(
          cause instanceof ApiError
            ? cause.message
            : "We could not reach the server. Check your connection and try again."
        );
        console.error("quote failed to load:", cause);
      });

    return () => controller.abort();
  }, [getToken, planCode, cycle, attempt]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  /**
   * Asks for the order again, with a code or without one.
   *
   * One path for both, because to the server they are one question. What is
   * held afterwards is only ever what came back: a quote that answers with no
   * coupon on it leaves the row empty even if a code was sent, which is the
   * server quietly declining to apply something it still priced.
   */
  const requote = useCallback(
    async (couponCode: string | null): Promise<AppliedCoupon | null> => {
      if (!planCode || !cycle) {
        // A bug at the call site: the coupon row is not drawn until there is a
        // plan to price.
        throw new Error("No plan to price.");
      }

      setPricing(true);
      try {
        const quoted = await fetchPrepaidQuote(getToken, {
          planCode,
          couponCode,
          months: MONTHS_PER_CYCLE[cycle],
        });

        setQuote(quoted);
        setStatus("ready");
        setError(undefined);
        return toApplied(quoted);
      } finally {
        setPricing(false);
      }
    },
    [getToken, planCode, cycle]
  );

  const apply = useCallback(
    (code: string) => requote(code.trim().toUpperCase()),
    [requote]
  );

  const remove = useCallback(async () => {
    await requote(null);
  }, [requote]);

  return {
    price: quote ? toBreakdown(quote) : null,
    coupon: quote ? toApplied(quote) : null,
    quote,
    status,
    error,
    pricing,
    apply,
    remove,
    reload,
  };
}

export default useQuote;
